import "./style.css";
import { useState, useEffect, useCallback } from "react";


import { fetchBranchData } from "../../redux/slices/branchSlice";
import { fetchDepartmentData } from "../../redux/slices/departmentSlice";
import { fetchData } from "../../redux/slices/shiftsSlice";

import { useDispatch } from "react-redux";

// Child Components
import PayrollConfigurationList from "./components/PayrollConfigurationList";
import PayrollConfiguration from "./components/PayrollConfiguration";
import PayrollSummary from "./components/PayrollSummary/index";
import RunPayroll from "./components/RunPayroll/index.js";
import Advance from "./components/Advance/index.js";
import Incentive from "./components/Incentive/index.js";
import Reimbursement from "./components/Reimbursement/index.js";
import PaymentHistory from "./components/PaymentHistory/index.js";
import TDS from "./components/TDS/index.js";
import AttendanceInput from "../Payroll/AttendanceInput1/index.js";
import { HasPermission } from "../../services/hasPermission.js";

// ─── Icon map ────────────────────────────────────────────────────────────────
const TAB_ICONS = {
  "payroll-configurations": "fa-gear",           // Settings/config gear
  "attendance-input":       "fa-user-clock",     // Person + clock = attendance
  "advance":                "fa-hand-holding-dollar", // Hand holding money = advance/incentive
  "reimbursement":          "fa-file-invoice",   // Invoice document = reimbursement
  "tds":                    "fa-scale-balanced", // Balance scale = tax/legal
  "payroll-summary":        "fa-chart-pie",      // Pie chart = summary/analytics
  "run-payroll":            "fa-circle-play",    // Play button = run/execute
  "payment-history":        "fa-rectangle-history", // History stack = payment history
};

// ─── Side Nav ────────────────────────────────────────────────────────────────
const PayrollSideNav = ({ tabs, activeTab, onTabChange }) => {
  const [expanded, setExpanded] = useState(false);

  const COLLAPSED_W = 56;
  const EXPANDED_W = 224;

  return (
    <aside
      className="payroll-sidenav"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{
        width: expanded ? EXPANDED_W : COLLAPSED_W,
        minWidth: expanded ? EXPANDED_W : COLLAPSED_W,
        height: "100%",
        borderRight: "1px solid var(--pay-border)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1), min-width 0.25s cubic-bezier(0.4,0,0.2,1)",
        overflow: "hidden",
        flexShrink: 0,
        zIndex: 10,
      }}
    >

      {/* ── Header: logo icon + "Payroll" title ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 13px",
          height: 56,
          borderBottom: "1px solid var(--pay-border)",
          flexShrink: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {/* Logo mark */}
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: "linear-gradient(135deg, #8660F8 0%, #5b3fd4 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 0 10px rgba(134,96,248,0.4)",
          }}
        >
          <i className="fa-solid fa-indian-rupee-sign" style={{ color: "#fff", fontSize: 13 }} />
        </div>

        {/* Title fades in on hover */}
        <div
          style={{
            opacity: expanded ? 1 : 0,
            transform: expanded ? "translateX(0)" : "translateX(-8px)",
            transition: "opacity 0.2s ease, transform 0.2s ease",
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--pay-text)", lineHeight: 1.2 }}>
            Payroll
          </div>
          <div style={{ fontSize: 10, color: "var(--pay-text-muted)", lineHeight: 1.2 }}>
            {new Date().toLocaleString("default", { month: "long" })} {new Date().getFullYear()}
          </div>
        </div>
      </div>

      {/* ── Nav Items ── */}
      <nav1
        style={{
          padding: "10px 8px",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {tabs
          .filter((t) => t.isAccess)
          .map((tab) => {
            const isActive = activeTab === tab.path;
            const icon = TAB_ICONS[tab.path] || "fa-circle";

            return (
              <div
                key={tab.path}
                onClick={() => onTabChange(tab.path)}
                title={!expanded ? tab.tab : ""}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 10px",
                  borderRadius: 10,
                  cursor: "pointer",
                  marginBottom: 3,
                  background: isActive ? "rgba(134,96,248,0.15)" : "transparent",
                  border: isActive ? "1px solid rgba(134,96,248,0.3)" : "1px solid transparent",
                  transition: "all 0.15s ease",
                  position: "relative",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "rgba(134,96,248,0.07)";
                    e.currentTarget.style.border = "1px solid rgba(134,96,248,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.border = "1px solid transparent";
                  }
                }}
              >
                {/* Active left bar */}
                {isActive && (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: "18%",
                      bottom: "18%",
                      width: 3,
                      borderRadius: "0 3px 3px 0",
                      background: "var(--pay-accent)",
                      boxShadow: "0 0 8px rgba(134,96,248,0.7)",
                    }}
                  />
                )}

                {/* Icon box */}
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: isActive
                      ? "rgba(134,96,248,0.22)"
                      : "rgba(169,163,201,0.07)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "background 0.15s",
                  }}
                >
                  <i
                    className={`fa-solid ${icon}`}
                    style={{
                      fontSize: 13,
                      color: isActive
                        ? "var(--pay-accent)"
                        : "var(--pay-text-muted)",
                      transition: "color 0.15s",
                    }}
                  />
                </div>

                {/* Label slides in on hover */}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive
                      ? "var(--pay-text)"
                      : "var(--pay-text-muted)",
                    opacity: expanded ? 1 : 0,
                    transform: expanded ? "translateX(0)" : "translateX(-6px)",
                    transition: "opacity 0.2s ease, transform 0.2s ease, color 0.15s",
                    pointerEvents: "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {tab.tab}
                </span>
              </div>
            );
          })}
      </nav1>

      {/* ── Bottom: active period badge ── */}
      <div
        style={{
          padding: "12px 8px",
          borderTop: "1px solid var(--pay-border)",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "rgba(134,96,248,0.1)",
            border: "1px solid rgba(134,96,248,0.22)",
            borderRadius: 8,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          <i
            className="fa-solid fa-calendar-days"
            style={{ color: "var(--pay-accent)", fontSize: 13, flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--pay-text)",
              opacity: expanded ? 1 : 0,
              transform: expanded ? "translateX(0)" : "translateX(-6px)",
              transition: "opacity 0.2s ease, transform 0.2s ease",
              pointerEvents: "none",
            }}
          >
            {new Date().toLocaleString("default", { month: "short" })} {new Date().getFullYear()}
          </span>
        </div>
      </div>
    </aside>
  );
};

// ─── Main Payroll Component ───────────────────────────────────────────────────
const Payroll = () => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user_data")) || {};

  const getData = useCallback(() => {
    dispatch(fetchBranchData(user.user_id));
    dispatch(fetchDepartmentData(user.user_id));
    dispatch(fetchData(user.user_id));
  }, []);

  // Auto-fetch branch, department and shift data on module mount
  // (matches original.js behaviour — all child components rely on these Redux slices)
  useEffect(() => {
    getData();
  }, [getData]);

  // ─── Tab order: Payroll Configuration is FIRST, rest follow ───────────────
  const tabs = [
    { tab: "Payroll Configuration", path: "payroll-configurations", isAccess: HasPermission("Payroll Configurations", "view") },
    { tab: "Attendance Input",      path: "attendance-input",       isAccess: HasPermission("Attendance Input", "view") },
    { tab: "Advance & Incentive",   path: "advance",                isAccess: HasPermission("Advance", "view") },
    { tab: "Reimbursement",         path: "reimbursement",          isAccess: HasPermission("Reimbursement", "view") },
    { tab: "TDS",                   path: "tds",                    isAccess: HasPermission("TDS", "view") },
    { tab: "Payroll Summary",       path: "payroll-summary",        isAccess: HasPermission("Payroll Summary", "view") },
    { tab: "Run Payroll",           path: "run-payroll",            isAccess: HasPermission("Run Payroll", "view") },
    { tab: "Payment History",       path: "payment-history",        isAccess: HasPermission("Payment History", "view") },
  ];

  const [activeTab, setActiveTab] = useState("");
  const [activeList, setActiveList] = useState(true);
  const [configKey, setConfigKey] = useState(0);

  // When switching to PayrollConfiguration (activeList = false), bump the key
  // so the component fully remounts and re-reads localStorage("payroll-data")
  const handleConfigChange = (val) => {
    setActiveList(val);
    if (!val) setConfigKey(k => k + 1); // force remount on edit
  };

  // ── Tab change — persist to sessionStorage so remounts restore position ──
  const handleTabChange = (path) => {
    sessionStorage.setItem("payrollActiveTab", path);
    setActiveTab(path);
  };

  useEffect(() => {
    // 1. Check for a handoff tab (e.g. from Advance → Reimbursement)
    const handoffTab = sessionStorage.getItem("payrollTab");
    if (handoffTab) {
      sessionStorage.removeItem("payrollTab");
      const match = tabs.find((t) => t.path === handoffTab && t.isAccess);
      if (match) { handleTabChange(match.path); return; }
    }

    // 2. Restore last active tab
    const savedTab = sessionStorage.getItem("payrollActiveTab");
    if (savedTab) {
      const match = tabs.find((t) => t.path === savedTab && t.isAccess);
      if (match) { setActiveTab(match.path); return; }
    }

    // 3. Default to first accessible tab
    const first = tabs.find((t) => t.isAccess);
    if (first) handleTabChange(first.path);
  }, []); // eslint-disable-line

  return (
    <div
      className="payroll-root"
      style={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Side Nav */}
      <PayrollSideNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Page Content */}
      <div
        className="page-body"
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {activeTab === "payroll-configurations" && (
          activeList
            ? <PayrollConfigurationList onChange={handleConfigChange} />
            : <PayrollConfiguration key={configKey} onChange={handleConfigChange} />
        )}
        
        {activeTab === "payroll-summary" && <PayrollSummary onNavigate={handleTabChange} />}
        {activeTab === "run-payroll" && <RunPayroll />}
        {activeTab === "attendance-input" && <AttendanceInput />}
        {activeTab === "advance" && <Advance onNavigate={handleTabChange} />}
        {activeTab === "incentive" && <Incentive />}
        {activeTab === "reimbursement" && <Reimbursement />}
        {activeTab === "tds" && <TDS />}
        {activeTab === "payment-history" && <PaymentHistory />}
      </div>
    </div>
  );
};

export default Payroll;